import React, { useCallback, useState } from 'react';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../services/translate';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { ClientError, NotFound } from '../../services/central-api/base-service';
import moment from 'moment';
import DeleteModal from '../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { AlreadySignedReport } from '../../services/central-api/dispatch';
import { appAgentService } from '../../services/central-api/app-agent';
import MyAppText from '../../components/MyAppText';
import ConfirmIdentityModal from '../../components/ConfirmIdentityModal';
import { openExternalLinkInNewTab } from '../../services/navigation-assistant';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-solid-svg-icons';

export default function AgentSignatures() {
    const { styles, theme } = useStyles(styleSheet);
    const [isLoading, setIsLoading] = useState(true);
    const [signableReports, setSignableReports] = useState<SignableReport[]>([]);
    const [confirmSignModalVisible, setConfirmSignModalVisible] = useState(false);
    const [selectedSignableReport, setSelectedSignableReport] = useState<SignableReport>();
    const [isConfirmIdentityModalVisible, setIsConfirmIdentityModalVisible] = useState(false);

    async function getPendingSignatures(): Promise<void> {
        try {
            setIsLoading(true);
            const signableReports = await appAgentService.getTeamPendingSignatures();
            setSignableReports(signableReports);

        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    async function teamSignReport(signableReportId: number, params: { password: string; }) {
        try {
            await appAgentService.teamSignReport(signableReportId, params);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            getPendingSignatures();
            return 'success';
        } catch (err) {
            if (err instanceof AlreadySignedReport) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message, { userName: err.userName }),
                });
                return 'failure';
            } else if (err instanceof NotFound) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            } else if (err instanceof ClientError) {
                if (err.message === 'InvalidValue') {
                    Toast.show({
                        type: 'sentinelxError',
                        text1: translate('invalidPassword'),
                    });
                    return 'failure';
                }
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return 'failure';
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getPendingSignatures();

            const interval = setInterval(() => {
                getPendingSignatures();
            }, 30000);

            return () => clearInterval(interval);
        }
    }, [isFocused]));

    async function openReportUrl(signableReport: SignableReport) {
        try {
            const res = await appAgentService.getSignableReportResultUrl(signableReport.id);
            openExternalLinkInNewTab(res.resultUrl);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }

    }

    function getReportProtocolIdByType(signableReport: SignableReport) {
        if (signableReport.reportType === 'occurrence') {
            return signableReport.occurrenceId;
        } else if (signableReport.reportType === 'rdp_ras') {
            return signableReport.unitId;
        }
        return undefined;
    }

    function getReportFinishedDateByType(signableReport: SignableReport) {
        if (signableReport.reportType === 'occurrence') {
            return moment(signableReport.occurrence?.finishedAt).format('DD/MM/YYYY HH:mm:ss');
        } else if (signableReport.reportType === 'rdp_ras') {
            return moment(signableReport.unit?.definitiveFinish).format('DD/MM/YYYY HH:mm:ss');
        }
        return undefined;
    }

    function getReportType(signableReport: SignableReport) {
        if (signableReport.reportType === 'occurrence') {
            return translate('occurrenceReportAcronym');
        } else if (signableReport.reportType === 'rdp_ras') {
            return `${translate('activityAndServiceAcronym')}/${translate('policePatrolScheduleAcronym')}`;
        }
        return undefined;
    }

    function renderItem(signableReport: SignableReport) {
        return <TouchableOpacity key={signableReport.id} onPress={() => {
            openReportUrl(signableReport);
            setSelectedSignableReport(signableReport);
            setConfirmSignModalVisible(true);
        }}>
            <View style={styles.card}>
                <View style={{ flex: 1 }}>
                    <View style={styles.overlineRow}>
                        <FontAwesomeIcon
                            icon={faFile}
                            fontSize={11}
                            color={theme.colors.neutral['400']}
                        />
                        <MyAppText style={styles.overlineText}>
                            {getReportType(signableReport)}
                        </MyAppText>
                    </View>
                    <MyAppText style={styles.mainText}>
                        {translate('nProtocol')}: {getReportProtocolIdByType(signableReport)}
                    </MyAppText>
                    <MyAppText style={styles.dateText}>
                        {signableReport.occurrence?.occurrenceType?.name || ''}
                    </MyAppText>
                    <MyAppText style={styles.dateText}>
                        {translate('finishedAt')}: {getReportFinishedDateByType(signableReport)}
                    </MyAppText>
                </View>
            </View>
        </TouchableOpacity>;
    }

    return (
        <>
            {isLoading ?
                <View style={styles.loadingView}>
                    <MyAppText style={styles.loadingText}>
                        {translate('loading')}...
                    </MyAppText>
                    <ActivityIndicator
                        animating={true}
                        style={styles.loading}
                        size='large'
                        color='#0071bc'
                    >
                    </ActivityIndicator>
                </View> :

                signableReports.length ?
                    <ScrollView>
                        <View style={styles.container}>
                            {signableReports.map((signableReport) => (
                                renderItem(signableReport)
                            ))}
                        </View>
                    </ScrollView>
                    : <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <MyAppText style={{ color: theme.colors.labelColor }}>
                            {translate('noResults')}.
                        </MyAppText>
                    </View>}
            {selectedSignableReport && <DeleteModal
                setModalVisible={setConfirmSignModalVisible}
                isModalVisible={confirmSignModalVisible}
                itemName={translate('object')}
                customConfirm={translate('sign')}
                customMessage={selectedSignableReport.reportType === 'occurrence'
                    ? translate('confirmSignature', { documentType: getReportType(selectedSignableReport!), occurrenceId: getReportProtocolIdByType(selectedSignableReport!) })
                    : translate('confirmTeamSignature', { documentType: getReportType(selectedSignableReport!), teamId: getReportProtocolIdByType(selectedSignableReport!) })
                }
                onSubmitType='primary'
                onSubmit={() => {
                    setIsConfirmIdentityModalVisible(true);
                }}
            />}
            <ConfirmIdentityModal
                isModalVisible={isConfirmIdentityModalVisible}
                setModalVisible={setIsConfirmIdentityModalVisible}
                onSubmit={async (password) => {
                    if (!selectedSignableReport) {
                        return 'success';
                    }
                    return await teamSignReport(selectedSignableReport.id, { password });
                }}

            />
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    loading: {
        marginHorizontal: 'auto',
        transform: [{ scaleX: 2 }, { scaleY: 2 }],
        width: '20%',
        height: '20%',
    },
    loadingText: {
        color: theme.colors.loadingColor,
        textAlign: 'center',
        fontSize: 16
    },
    loadingView: {
        flex: 1,
        justifyContent: 'center',
        alignContent: 'center'
    },
    container: {
        flex: 1,
        padding: 16,
        rowGap: 10
    },
    card: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.card.border,
        padding: 8,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: theme.colors.card.background,
        height: 80,
        flex: 1
    },
    mainText: {
        color: theme.colors.textColor,
        fontSize: 15,
        flex: 1
    },
    dateText: {
        color: theme.colors.card.detailText,
        fontSize: 12,
    },
    image: {
        height: 64,
        width: 64,
        borderRadius: 4,
        objectFit: 'contain',
    },
    imageBox: {
        height: 64,
        width: 64,
        borderRadius: 4,
        justifyContent: 'center',
        alignItems: 'center'
    },
    overlineRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        marginBottom: 6,
    },
    overlineText: {
        fontSize: 11,
        fontWeight: '600',
        letterSpacing: 0.3,
        color: theme.colors.neutral['400'],
        textTransform: 'uppercase',
    },
}));
